import { aiccInstance } from "../utils/instance";
import {
  AiccConversationAudioResponse,
  AiccConversationDetailResponse,
  AiccConversationListResponse,
  AiccFeedbackAnalyticsResponse,
  AiccFeedbackListResponse,
  AiccLanguageResponse,
  AiccOnboardingProfileResponse,
  AiccSubUserDetailResponse,
  AiccSubUserListResponse,
  AiccSystemSettingsResponse,
  AiccUserCanDoStatementsResponse,
  AiccUserVocabularyResponse,
  CustomerAiccResponse,
} from "../types/dto/response/aiccCustomer";
import {
  AiccConversationListRequest,
  AiccFeedbackAnalyticsRequest,
  AiccFeedbackFilterRequest,
  AiccFeedbackListRequest,
  AiccSubUserListRequest,
} from "../types/dto/request/aiccCustomer";
import { AiccSystemSettings } from "../types/model/aiccCustomer";
import {
  transformAiccConversationAudio,
  transformAiccConversationDetail,
  transformAiccConversationList,
  transformAiccFeedbackAnalytics,
  transformAiccFeedbackList,
  transformAiccOnboardingProfile,
  transformAiccSubUserDetail,
  transformAiccSubUserList,
  transformAiccSystemSettings,
  transformAiccSystemSettingsRequest,
  transformAiccUserCanDoStatements,
  transformAiccUserVocabulary,
  transformCustomerAicc,
} from "../types/transformers/aiccCustomer";

export async function fetchCustomerAiccInfo(customerId: string) {
  const url = `/customers/${customerId}/aicc/quota`;
  return aiccInstance
    .get<CustomerAiccResponse>(url)
    .then((response) => (response ? transformCustomerAicc(response) : undefined));
}

export async function resetCustomerQuotas(customerId: string) {
  const url = `/customers/${customerId}/aicc/quota/reset`;
  return aiccInstance.post<void>(url);
}

/**
 * Wipes a customer's conversation history across all of their sub-users.
 *
 * The backend deletes only completed conversations; an active one is left running.
 * So there is nothing to filter client-side, and the dialog's "all historical
 * conversations" wording stays accurate — an in-flight conversation is not history.
 */
export async function deleteCustomerConversations(customerId: string) {
  return aiccInstance.delete<void>(`/customers/${customerId}/conversations`);
}

export async function fetchAiccSystemSettings() {
  return aiccInstance
    .get<AiccSystemSettingsResponse>("/system-settings")
    .then((response) => transformAiccSystemSettings(response));
}

export async function updateAiccSystemSettings(settings: AiccSystemSettings) {
  return aiccInstance
    .put<AiccSystemSettingsResponse>("/system-settings", transformAiccSystemSettingsRequest(settings))
    .then((response) => transformAiccSystemSettings(response));
}

export async function fetchAiccLanguages() {
  return aiccInstance.get<AiccLanguageResponse[]>("/all-languages").then((response) => response ?? []);
}

export async function fetchAiccFeedbackAnalytics(request: AiccFeedbackAnalyticsRequest) {
  return aiccInstance
    .get<AiccFeedbackAnalyticsResponse>("/feedback/analytics", { params: request })
    .then((response) => transformAiccFeedbackAnalytics(response));
}

export async function fetchAiccFeedbackList(request: AiccFeedbackListRequest) {
  return aiccInstance
    .get<AiccFeedbackListResponse>("/feedback", { params: request })
    .then((response) => transformAiccFeedbackList(response));
}

export async function exportAiccFeedbackList(request: AiccFeedbackFilterRequest) {
  return aiccInstance.get<Blob>("/feedback/export", { params: request, responseType: "blob" });
}

export async function fetchAiccSubUsers(request: AiccSubUserListRequest) {
  return aiccInstance
    .get<AiccSubUserListResponse>("/users", { params: request })
    .then((response) => transformAiccSubUserList(response));
}

export async function fetchAiccSubUserDetail(subUserId: string) {
  return aiccInstance
    .get<AiccSubUserDetailResponse>(`/users/${subUserId}`)
    .then((response) => (response ? transformAiccSubUserDetail(response) : undefined));
}

export async function fetchAiccOnboardingProfile(subUserId: string, languageId: number) {
  return aiccInstance
    .get<AiccOnboardingProfileResponse>(`/users/${subUserId}/languages/${languageId}/onboarding-profile`)
    .then((response) => (response ? transformAiccOnboardingProfile(response) : undefined));
}

export async function fetchAiccUserVocabulary(subUserId: string, languageId: number) {
  const url = `/users/${subUserId}/languages/${languageId}/vocabulary`;
  return aiccInstance
    .get<AiccUserVocabularyResponse>(url)
    .then((response) => (response ? transformAiccUserVocabulary(response) : undefined));
}

export async function fetchAiccUserCanDoStatements(subUserId: string, languageId: number) {
  return aiccInstance
    .get<AiccUserCanDoStatementsResponse>(`/users/${subUserId}/languages/${languageId}/can-do-statements`)
    .then((response) => (response ? transformAiccUserCanDoStatements(response) : undefined));
}

export async function fetchAiccConversations(
  subUserId: string,
  languageId: number,
  request: AiccConversationListRequest
) {
  return aiccInstance
    .get<AiccConversationListResponse>(`/users/${subUserId}/languages/${languageId}/conversations`, { params: request })
    .then((response) => transformAiccConversationList(response));
}

export async function deleteAiccConversation(subUserId: string, languageId: number, conversationId: string) {
  return aiccInstance.delete<void>(`/users/${subUserId}/languages/${languageId}/conversations/${conversationId}`);
}

export async function fetchAiccConversationDetail(subUserId: string, languageId: number, conversationId: string) {
  return aiccInstance
    .get<AiccConversationDetailResponse>(
      `/users/${subUserId}/languages/${languageId}/conversations/${conversationId}/review`
    )
    .then((response) => (response ? transformAiccConversationDetail(response) : undefined));
}

export async function fetchAiccConversationAudio(subUserId: string, languageId: number, conversationId: string) {
  return aiccInstance
    .get<AiccConversationAudioResponse>(
      `/users/${subUserId}/languages/${languageId}/conversations/${conversationId}/audio`
    )
    .then((response) => transformAiccConversationAudio(response));
}
